<template>
	<div>
		<div>
			<BaseHeader :title="$t('rental_car_pricing_by_classes').toString()">
				<div class="flex items-center mr-8" v-if="isUserAllowedInLevel5or6">
					<div class="inline-flex mr-8 w-80">
						<BaseSelect
							v-if="getListOfLicensePlates"
							ref="BaseSelect.vehicle"
							:filter-only="true"
							:placeholder="$t('pick_rental_car')"
							:options="getListOfLicensePlates"
							v-model="selectedRentalCar"
							class="w-80 mt-4"
						/>
					</div>

					<div class="inline-flex">
						<span
							class="border-l border-t border-b font-bold rounded-bl rounded-tl w-16 text-center"
							:class="{ 'bg-sp border-sp text-white': isPriceNet, 'hover:bg-gray-200 cursor-pointer': !isPriceNet }"
							@click="toggleVatPrices('net')"
							>{{ $t('net') }}</span
						>
						<span
							class="border-l border-r border-t border-b font-bold rounded-tr rounded-br w-16 text-center"
							:class="{ 'bg-sp border-sp text-white': !isPriceNet, 'hover:bg-gray-200 cursor-pointer': isPriceNet }"
							@click="toggleVatPrices('gross')"
							>{{ $t('gross') }}</span
						>
					</div>
				</div>
			</BaseHeader>
			<div v-if="!isLoading">
				<div class="flex" :class="{ 'mr-12': !isRepairShopPricingEditable }">
					<div class="w-full flex">
						<div class="flex mx-8">
							<RentalCarPricingRepairShopForm
								v-if="repairShopPricings"
								:repair-shop-pricings="repairShopPricings"
								:editable="isRepairShopPricingEditable"
								:selected-rental-car-class="selectedRentalCarClass"
							></RentalCarPricingRepairShopForm>
						</div>
					</div>
				</div>
				<div class="w-full my-8 pr-8 text-right" v-if="isRepairShopPricingEditable">
					<BaseButton @click="save" class="btn-blue">{{ $t('general.save') }}</BaseButton>
				</div>
			</div>
			<LoadingSpinner v-else />
		</div>
		<BaseHeader :title="$t('extra_costs_individual_rent_options').toString()" :subtitle="$t('fill_with_zero').toString()" />
		<div v-if="!isLoadingExtras">
			<div class="w-full">
				<div class="ml-8 mt-8 flex font-bold" style="font-size: 20px">{{ $t('one_time_options') }}</div>
				<div class="flex grid grid-cols-3 mx-8 mt-4 gap-x-8 gap-y-2" v-if="variablesOnce">
					<CompanyVariable
						:title="$t('transport_service_landlord')"
						:variable="variable"
						:required="true"
						v-for="variable in variablesOnce"
						:key="variable.id"
						:ref="'variables_once_' + variable.id"
					/>
				</div>
			</div>
			<div class="w-full">
				<div class="ml-8 mt-8 flex font-bold" style="font-size: 20px">{{ $t('options_with_daily_costs') }}</div>
				<div class="flex grid grid-cols-3 mx-8 mt-4 gap-x-8 gap-y-2" v-if="variables">
					<CompanyVariable
						:title="$t('transport_service_landlord')"
						:variable="variable"
						:required="true"
						v-for="variable in variables"
						:key="variable.id"
						:ref="'variables_' + variable.id"
					/>
				</div>
				<div class="mx-8 mt-4 mr-24" v-if="variableCustom">
					<div class="flex w-full font-bold mb-2" style="color: #495057">{{ $t('customizable_input_field') }}</div>
					<div class="flex flex-row gap-8">
						<BaseInput v-model="variableCustom.label" @input="$emit('input')" ref="text" class="w-1/3" :required="false" />
						<BaseInputNumber v-model="variableCustom.value" ref="number" class="w-1/3" :precision="2" :required="false" />
					</div>
				</div>
			</div>
			<div class="w-full">
				<div class="flex justify-end m-8">
					<BaseButton @click="saveNebenkosten" class="btn-blue">{{ $t('general.save') }}</BaseButton>
				</div>
			</div>
		</div>
		<LoadingSpinner v-else />
	</div>
</template>

<script>
import BaseHeader from '@/components/navigation/BaseHeader';
import Authentication from '@/mixins/Authentication';
import RentalCarPricingRepairShopForm from '@/components/rentalcars/forms/RentalCarPricingRepairShopForm';
import { RentalCarsApiFactory, VehicleApiFactory } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import BaseSelect from '@/components/forms/inputs/BaseSelect';
import BaseButton from '@/components/forms/buttons/BaseButton';
import CompanyVariable from '@/views/usermanagment/CompanyVariable';
import Validate from '@/mixins/Validate';
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';
import BaseInput from '@/components/forms/inputs/BaseInput.vue';
import RentalCarExtraCostsRepository from '@/api/repositories/RentalCarExtraCostsRepository';
import BaseInputNumber from '@/components/forms/inputs/BaseInputNumber.vue';

export default {
	name: 'RentalCarPricingRepairShop',
	components: {
		BaseInputNumber,
		BaseInput,
		LoadingSpinner,
		CompanyVariable,
		BaseButton,
		BaseSelect,
		RentalCarPricingRepairShopForm,
		BaseHeader,
	},
	mixins: [Authentication, Validate],
	data() {
		return {
			isPriceNet: true,
			repairShopPricings: {},
			repairShopPricingsNet: null,
			repairShopPricingsGross: null,
			rentalCars: null,
			selectedRentalCar: null,
			selectedRentalCarClass: null,
			variables: [],
			variablesOnce: [],
			variableCustom: null,
			editValue: false,
			isLoading: false,
			isLoadingExtras: false,
		};
	},
	created() {
		this.isLoading = true;
		this.loadRepairShopPricing();
		this.getRentalCars();
	},
	mounted() {
		this.getRentalCarExtraCosts();
	},
	watch: {
		selectedRentalCar: function() {
			if (this.selectedRentalCar.value === null) {
				this.selectedRentalCarClass = null;
			} else {
				this.selectedRentalCarClass = this.selectedRentalCar.value.rentalCarClass;
			}
		},
		isPriceNet() {
			if (this.isPriceNet) {
				this.repairShopPricings = this.repairShopPricingsNet;
			} else {
				this.repairShopPricings = this.repairShopPricingsGross;
			}
		},
	},
	computed: {
		getListOfLicensePlates() {
			const plates = [];
			if (this.rentalCars) {
				const rentalCars = this.rentalCars.map(d => {
					return d.vehicle;
				});

				plates.push({
					value: null,
					display: this.$t('no_selection'),
				});

				for (let i = 0; i < rentalCars.length; i++) {
					const isRented = rentalCars && rentalCars[i].rentalContracts ? ' – ' + this.$t('currently_rented') : '';
					if (rentalCars[i].rentalCarClass) {
						plates.push({
							value: rentalCars[i],
							grayed: isRented,
							display:
								rentalCars[i].licensePlate.text +
								' (' +
								rentalCars[i].manufacturer +
								' ' +
								rentalCars[i].model +
								') - Klasse ' +
								rentalCars[i].rentalCarClass +
								isRented,
						});
					}
				}

				return [...plates.filter(plate => !plate.grayed), ...plates.filter(plate => plate.grayed)];
			}
			return null;
		},
		isRepairShopPricingEditable() {
			return this.isAuthenticatedUserInGroupAdmin && (this.isUserAllowedInLevel5 || this.isUserAllowedInLevel6);
		},
	},
	methods: {
		getRentalCarExtraCosts() {
			this.isLoadingExtras = true;
			new RentalCarExtraCostsRepository()
				.getAll(false)
				.then(response => {
					this.variables = response.data.daily.filter(variable => !variable.key.includes('custom_field'));
					this.variablesOnce = response.data.once;
					if (!response.data.daily.find(variable => variable.key.includes('custom_field'))) {
						this.variableCustom = { id: null, key: 'custom_field', label: '', value: null, type: 'number' };
					} else {
						this.variableCustom = response.data.daily.find(variable => variable.key.includes('custom_field'));
					}
					this.isLoadingExtras = false;
				})
				.catch(e => {
					this.isLoadingExtras = false;
					console.log(e);
				});
		},
		saveNebenkosten() {
			if (!this.validate()) {
				this.$swal(this.$t('general.error').toString(), this.$t('fill_zero_hint').toString(), 'error');
				return false;
			}

			if (!this.variableCustom.value || isNaN(this.variableCustom.value)) {
				if (this.variableCustom.label) {
					return this.$swal(this.$t('general.error').toString(), this.$t('custom_field_error').toString(), 'error');
				} else {
					this.variableCustom.value = null;
				}
			} else if (!this.variableCustom.label) {
				return this.$swal(this.$t('general.error').toString(), this.$t('custom_field_label_error').toString(), 'error');
			}
			if (this.variableCustom.label === '0') {
				this.variableCustom.value = null;
				this.variableCustom.label = '';
			}

			const rentalCarExtraCosts = this.variablesOnce.concat(this.variables, [this.variableCustom]);

			new RentalCarExtraCostsRepository()
				.save({ extraCosts: rentalCarExtraCosts })
				.then(response => {
					this.$swal(this.$t('general.success').toString(), this.$t('extra_costs_saved').toString(), 'success').then(_ => {
						this.$store.dispatch('updateAuthenticatedUser');
						this.getRentalCarExtraCosts();
						this.$store.commit('setConfirmLeaving', false);
					});
				})
				.catch(e => {
					console.log(e);
					this.$swal(this.$t('general.error').toString(), this.$t('extra_costs_save_error').toString(), 'error');
				});
		},
		save() {
			RentalCarsApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.rentalcarpricingPut(this.repairShopPricings)
				.then(response => {
					this.loadRepairShopPricing();
					this.$swal(this.$t('general.success').toString(), this.$t('data_saved').toString(), 'success');
				})
				.catch(error => {});
		},
		getRentalCars() {
			VehicleApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.rentalcarsGet()
				.then(result => {
					this.rentalCars = result.data.data;
					this.isLoading = false;
				})
				.catch(error => {
					this.isLoading = false;
					this.$swal(this.$t('general.error').toString(), this.$t('rental_car_contract_error').toString(), 'error');
				});
		},
		toggleVatPrices(vatType) {
			if (vatType === 'gross') {
				this.isPriceNet = false;
			} else {
				this.isPriceNet = true;
			}
		},
		loadRepairShopPricing() {
			RentalCarsApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.rentalcarpricingGet()
				.then(response => {
					if (this.isAuthenticatedUserInGroupAdmin && (this.isUserAllowedInLevel5 || this.isUserAllowedInLevel6)) {
						this.repairShopPricings = response.data.net;
					} else {
						this.repairShopPricings = response.data.gross;
					}
					this.repairShopPricingsNet = response.data.net;

					this.repairShopPricingsGross = response.data.gross;
				})
				.catch(error => {});
		},
	},
};
</script>

<style scoped></style>
